import {
  Media,
  MediaStatus,
  MediaType,
  PickerMode,
  useCurrentAccount,
  useCurrentAccountAvatar,
  useUpdateAccount,
} from '@gripp/shared-logic';
import { useRef, useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { AccountAvatar } from './accountAvatar';
import { RenderMediaItem } from './media';
import { ImageData, ImagePickerComponent, SizePrefix } from './media/types';
import { useTypedMediaItem } from './media/useTypedMediaItem';

export type CurrentAccountAvatarProps = {
  mediaUrl: string;
  size: number;
  onPress?: any;
  editable?: boolean;
  imagePicker?: ImagePickerComponent | undefined;
  isIconInBox?: boolean;
  allowsEditingAvatar?: boolean;
};

export const CurrentAccountAvatar = ({
  mediaUrl,
  size,
  onPress = () => {},
  editable = false,
  imagePicker = undefined,
  isIconInBox = false,
  allowsEditingAvatar = false,
}: CurrentAccountAvatarProps) => {
  const ImagePicker = imagePicker;
  const openPickerRef = useRef<(() => void) | null>(null);
  const { account } = useCurrentAccount();
  const { currentAvatar } = useCurrentAccountAvatar();
  const {
    typedMediaItem: newAvatar,
    handleSet,
    handleUploadProgress,
  } = useTypedMediaItem<ImageData>(undefined);
  const [showImage, setShowImage] = useState(true);

  const { mutateAsync: updateAccount } = editable
    ? useUpdateAccount()
    : { mutateAsync: () => {} };

  const updateAvatar = async (image: Media) => {
    await updateAccount({
      input: {
        id: account?.id ?? '',
        avatar: {
          id: image.id,
        },
      },
    });
  };

  if (editable && ImagePicker) {
    const avatarSource = newAvatar?.url
      ? { uri: newAvatar.url }
      : currentAvatar?.status !== 'PENDING' && currentAvatar?.id
      ? {
          uri: `${mediaUrl}/images/${currentAvatar.id}/xs.${currentAvatar.extension}`,
        }
      : undefined;

    return (
      <View style={{ position: 'relative' }}>
        <ImagePicker
          mode={PickerMode.CAMERA}
          IsSquareCameraView={true}
          setMedia={(media: ImageData) => {
            setShowImage(true);
            handleSet(media, updateAvatar);
          }}
          setProgress={handleUploadProgress}
          setUploading={() => {}}
          isIconInBox={isIconInBox}
          allowsEditing={allowsEditingAvatar}
          onPickerReady={(openPicker) => {
            openPickerRef.current = openPicker;
          }}
          buttonComponent={<View style={styles.hiddenButton} />}
        />
        {showImage && avatarSource && (
          <RenderMediaItem
            mediaItem={{
              url: avatarSource.uri,
              id: `${currentAvatar?.id ?? 'new-avatar'}`,
              type: MediaType.Image,
              status: MediaStatus.Processed,
              name: '',
              extension: newAvatar?.extension ?? currentAvatar?.extension ?? '',
              isUploading: newAvatar?.isUploading,
              uploadProgress: newAvatar?.uploadProgress,
              showProcessing: currentAvatar?.status === 'PENDING',
              urlsBySize: {
                [SizePrefix.extraSmall]: avatarSource.uri,
                [SizePrefix.small]: avatarSource.uri,
                [SizePrefix.original]: avatarSource.uri,
              },
            }}
            displayWidth={size}
            hasLightbox
            deleteMediaItem={() => openPickerRef.current?.()}
            canDelete
            style={styles.mediaItemContainer}
          />
        )}
      </View>
    );
  }

  const sizeCircle = { width: size, height: size };
  return (
    <TouchableOpacity onPress={onPress}>
      <View style={[styles.circleContainer, sizeCircle]}>
        <AccountAvatar
          avatar={currentAvatar}
          mediaUrl={mediaUrl}
          size={size}
          style={styles.mainAvatar}
          directAvatarUrl={newAvatar?.url}
        />
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  circleContainer: {
    borderRadius: 50,
    overflow: 'hidden',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  mainAvatar: {
    position: 'absolute',
  },
  mediaItemContainer: {
    paddingRight: 0,
    paddingTop: 0,
  },
  hiddenButton: {
    width: 0,
    height: 0,
    overflow: 'hidden',
  },
});
